import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import { UPLOADS_URL } from '../config';
import DjamannLoader from '../components/DjamannLoader';
import { Package, Trash2, CheckCircle, AlertTriangle, HelpCircle, XCircle, Plus, Star, Pencil, Search, BarChart3, Clock, Ban, ClipboardList, Puzzle, Calendar, Lock, Globe, Lightbulb, Paperclip, Check, X as XIcon, Save } from 'lucide-react';

const AdminSubscriptionPlans = () => {
  useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // plans, pending, all, modules, periods
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [moduleModal, setModuleModal] = useState({ show: false, module: null });
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Permissions admin
  const [adminRole, setAdminRole] = useState(null);
  
  // Modals de confirmation
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, type: 'warning' });
  const [rejectModal, setRejectModal] = useState({ show: false, subscriptionId: null, note: '' });
  const [periodModal, setPeriodModal] = useState({ show: false, months: '', label: '', discount: '0' });
  const [planModal, setPlanModal] = useState({ show: false, plan: null });
  
  // Filtres et pagination pour l'onglet "Tous"
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Recherche et pagination pour l'onglet "Modules"
  const [moduleSearch, setModuleSearch] = useState('');
  const [moduleCurrentPage, setModuleCurrentPage] = useState(1);
  const [moduleItemsPerPage, setModuleItemsPerPage] = useState(10);

  // Filtre période pour les revenus
  const [revenuePeriod, setRevenuePeriod] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const res = await api.get('/admin/team/permissions');
        setAdminRole(res.data.admin_role);
        // Si comptable, démarrer sur 'pending', sinon 'plans'
        if (res.data.admin_role === 'accountant') {
          setTab('pending');
        } else {
          setTab('plans');
        }
        loadData();
      } catch (err) {
        console.error(err);
        loadData();
      }
    };
    loadPermissions();
  }, []);

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const loadData = async (period = 'all') => {
    try {
      setLoading(true);
      const [plansRes, modulesRes, statsRes, periodsRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/admin/subscriptions/modules'),
        api.get(`/admin/subscriptions/stats?period=${period}`),
        api.get('/admin/subscriptions/periods')
      ]);
      setPlans(plansRes.data);
      setModules(modulesRes.data);
      setStats(statsRes.data);
      setPeriods(periodsRes.data || []);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (period, startDate = null, endDate = null) => {
    try {
      let url = `/admin/subscriptions/stats?period=${period}`;
      if (period === 'custom' && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      const statsRes = await api.get(url);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  // Recharger les stats quand la période change
  useEffect(() => {
    if (!loading) {
      if (revenuePeriod === 'custom') {
        loadStats(revenuePeriod, customDateRange.start, customDateRange.end);
      } else {
        loadStats(revenuePeriod);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenuePeriod, customDateRange.start, customDateRange.end]);

  const loadSubscriptions = async (status = null) => {
    try {
      const endpoint = status === 'pending' 
        ? '/admin/subscriptions/pending' 
        : `/admin/subscriptions${status ? `?status=${status}` : ''}`;
      const res = await api.get(endpoint);
      setSubscriptions(res.data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  useEffect(() => {
    if (tab === 'pending') {
      loadSubscriptions('pending');
    } else if (tab === 'all') {
      loadSubscriptions();
    }
  }, [tab]);

  const handleUpdatePlan = async (planId) => {
    try {
      await api.put(`/admin/subscriptions/plans/${planId}`, editingPlan);
      setEditingPlan(null);
      loadData();
      showToast('Pack mis à jour');
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  // === Gestion des packs (CRUD) ===
  const handleCreatePlan = () => {
    setPlanModal({
      show: true,
      plan: {
        code: '',
        nom: '',
        description: '',
        prix_mensuel: '',
        prix_annuel: '',
        couleur: '#6366F1',
        icone: '📦',
        is_popular: 0,
        is_active: 1,
        display_order: plans.length + 1,
        modules: []
      }
    });
  };

  const submitCreatePlan = async () => {
    if (!planModal.plan.code || !planModal.plan.nom || !planModal.plan.prix_mensuel) {
      showToast('Code, nom et prix mensuel requis', 'error');
      return;
    }
    try {
      await api.post('/admin/subscriptions/plans', planModal.plan);
      setPlanModal({ show: false, plan: null });
      loadData();
      showToast('Pack créé avec succès');
    } catch (err) {
      showToast('Erreur: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleDeletePlan = (plan) => {
    setConfirmModal({
      show: true,
      title: 'Supprimer le pack',
      message: `Êtes-vous sûr de vouloir supprimer le pack "${plan.nom}" ?\n\nCette action est irréversible.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/subscriptions/plans/${plan.id}`);
          loadData();
          showToast('Pack supprimé avec succès');
        } catch (err) {
          showToast('Erreur: ' + (err.response?.data?.error || err.message), 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleApprove = (subscriptionId) => {
    setConfirmModal({
      show: true,
      title: 'Approuver l\'abonnement',
      message: 'Êtes-vous sûr de vouloir approuver cet abonnement ? L\'utilisateur aura immédiatement accès aux fonctionnalités de son pack.',
      type: 'success',
      onConfirm: async () => {
        try {
          await api.post(`/admin/subscriptions/${subscriptionId}/approve`);
          loadSubscriptions(tab === 'pending' ? 'pending' : null);
          loadData();
          showToast('Abonnement approuvé');
        } catch (err) {
          showToast('Erreur: ' + err.message, 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleReject = (subscriptionId) => {
    setRejectModal({ show: true, subscriptionId, note: '' });
  };

  const submitReject = async () => {
    try {
      await api.post(`/admin/subscriptions/${rejectModal.subscriptionId}/reject`, { note: rejectModal.note });
      loadSubscriptions(tab === 'pending' ? 'pending' : null);
      loadData();
      showToast('Abonnement rejeté');
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
    setRejectModal({ show: false, subscriptionId: null, note: '' });
  };

  // === Gestion des périodes ===
  const handleUpdatePeriod = async (periodId) => {
    try {
      await api.put(`/admin/subscriptions/periods/${periodId}`, editingPeriod);
      setEditingPeriod(null);
      loadData();
      showToast('Période mise à jour');
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleCreatePeriod = () => {
    setPeriodModal({ show: true, months: '', label: '', discount: '0' });
  };

  const submitCreatePeriod = async () => {
    if (!periodModal.months || !periodModal.label) {
      showToast('Veuillez remplir tous les champs', 'error');
      return;
    }
    try {
      await api.post('/admin/subscriptions/periods', {
        months: parseInt(periodModal.months),
        label: periodModal.label,
        discount: parseFloat(periodModal.discount) || 0,
        is_active: true
      });
      loadData();
      showToast('Période créée');
      setPeriodModal({ show: false, months: '', label: '', discount: '0' });
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleDeletePeriod = (periodId) => {
    setConfirmModal({
      show: true,
      title: '🗑️ Supprimer la période',
      message: 'Êtes-vous sûr de vouloir supprimer cette période ? Les utilisateurs ne pourront plus la sélectionner.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/subscriptions/periods/${periodId}`);
          loadData();
          showToast('Période supprimée');
        } catch (err) {
          showToast('Erreur: ' + err.message, 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  // === Gestion des modules ===
  const handleCreateModule = () => {
    setModuleModal({
      show: true,
      module: {
        code: '',
        nom: '',
        description: '',
        icone: '📦',
        route_pattern: '',
        is_active: true
      }
    });
  };

  const handleEditModule = (mod) => {
    setModuleModal({
      show: true,
      module: { ...mod }
    });
  };

  const handleSaveModule = async () => {
    const mod = moduleModal.module;
    if (!mod.code || !mod.nom) {
      showToast('Code et nom requis', 'error');
      return;
    }
    
    try {
      if (mod.id) {
        // Mise à jour
        await api.put(`/admin/subscriptions/modules/${mod.id}`, mod);
        showToast('Module mis à jour');
      } else {
        // Création
        await api.post('/admin/subscriptions/modules', mod);
        showToast('Module créé');
      }
      setModuleModal({ show: false, module: null });
      loadData();
    } catch (err) {
      showToast('Erreur: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleToggleModuleActive = async (mod) => {
    try {
      await api.put(`/admin/subscriptions/modules/${mod.id}`, {
        is_active: !mod.is_active
      });
      loadData();
      showToast(mod.is_active ? 'Module désactivé' : 'Module activé');
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleDeleteModule = (mod) => {
    setConfirmModal({
      show: true,
      title: '🗑️ Supprimer le module',
      message: `Êtes-vous sûr de vouloir supprimer le module "${mod.nom}" ?\n\nCette action est irréversible et retirera ce module de tous les packs.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/subscriptions/modules/${mod.id}`);
          loadData();
          showToast('Module supprimé avec succès');
        } catch (err) {
          showToast('Erreur: ' + (err.response?.data?.error || err.message), 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  // Filtrage des abonnements
  const filteredSubscriptions = subscriptions.filter(s => {
    // Filtre de recherche
    const matchesSearch = !search || 
      s.user_nom?.toLowerCase().includes(search.toLowerCase()) ||
      s.user_email?.toLowerCase().includes(search.toLowerCase()) ||
      s.plan_nom?.toLowerCase().includes(search.toLowerCase());
    
    // Filtre par statut
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    
    // Filtre par pack
    const matchesPlan = planFilter === 'all' || s.plan_id?.toString() === planFilter;
    
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const paginatedSubscriptions = filteredSubscriptions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, planFilter, tab]);

  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg ${
            toast.type === 'success' ? 'bg-green-500 text-white' :
            toast.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <span className="text-xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
            </span>
            <p>{toast.message}</p>
          </div>
        </div>
      )}

      {/* Modal de Confirmation */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-scale-in">
            <div className="text-center">
              <span className="text-5xl">
                {confirmModal.type === 'success' ? '✅' : confirmModal.type === 'danger' ? '⚠️' : '❓'}
              </span>
              <h3 className="text-xl font-bold text-gray-900 mt-4">{confirmModal.title}</h3>
              <p className="text-gray-600 mt-2 whitespace-pre-line">{confirmModal.message}</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmModal({ show: false })}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className={`flex-1 px-4 py-3 text-white rounded-xl font-medium transition ${
                  confirmModal.type === 'danger' ? 'bg-red-500 hover:bg-red-600' :
                  confirmModal.type === 'success' ? 'bg-green-500 hover:bg-green-600' :
                  'bg-purple-500 hover:bg-purple-600'
                }`}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Rejet */}
      {rejectModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-scale-in">
            <div className="text-center mb-4">
              <span className="text-5xl">❌</span>
              <h3 className="text-xl font-bold text-gray-900 mt-4">Rejeter l'abonnement</h3>
              <p className="text-gray-600 mt-2">Vous pouvez ajouter une note explicative pour l'utilisateur.</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Raison du rejet (optionnel)</label>
              <textarea
                value={rejectModal.note}
                onChange={(e) => setRejectModal(prev => ({ ...prev, note: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                placeholder="Ex: Document illisible, informations incorrectes..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal({ show: false, subscriptionId: null, note: '' })}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={submitReject}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition"
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Création de Période */}
      {periodModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">➕ Nouvelle période</h3>
              <button
                onClick={() => setPeriodModal({ show: false, months: '', label: '', discount: '0' })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de mois</label>
                <input
                  type="number"
                  min="1"
                  value={periodModal.months}
                  onChange={(e) => setPeriodModal(prev => ({ ...prev, months: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Ex: 6"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label affiché</label>
                <input
                  type="text"
                  value={periodModal.label}
                  onChange={(e) => setPeriodModal(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Ex: 6 mois"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Réduction (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={periodModal.discount}
                  onChange={(e) => setPeriodModal(prev => ({ ...prev, discount: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Ex: 15"
                />
                {periodModal.discount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    Les utilisateurs bénéficieront de {periodModal.discount}% de réduction
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setPeriodModal({ show: false, months: '', label: '', discount: '0' })}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={submitCreatePeriod}
                className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Création/Edition de Pack */}
      {planModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 animate-scale-in my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">➕ Nouveau pack d'abonnement</h3>
              <button
                onClick={() => setPlanModal({ show: false, plan: null })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code (unique)</label>
                <input
                  type="text"
                  value={planModal.plan?.code || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') } }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="ex: premium, pro"
                />
              </div>
              
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du pack</label>
                <input
                  type="text"
                  value={planModal.plan?.nom || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, nom: e.target.value } }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="ex: Pack Premium"
                />
              </div>
              
              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={planModal.plan?.description || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, description: e.target.value } }))}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  placeholder="Décrivez les avantages de ce pack..."
                />
              </div>
              
              {/* Prix mensuel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix mensuel (XOF)</label>
                <input
                  type="number"
                  value={planModal.plan?.prix_mensuel || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, prix_mensuel: e.target.value } }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="ex: 5000"
                />
              </div>
              
              {/* Prix annuel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix annuel (XOF) - optionnel</label>
                <input
                  type="number"
                  value={planModal.plan?.prix_annuel || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, prix_annuel: e.target.value } }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="ex: 50000"
                />
              </div>
              
              {/* Icône */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icône (emoji)</label>
                <input
                  type="text"
                  value={planModal.plan?.icone || ''}
                  onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, icone: e.target.value } }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 text-2xl text-center"
                  placeholder="📦"
                />
              </div>
              
              {/* Couleur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={planModal.plan?.couleur || '#6366F1'}
                    onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, couleur: e.target.value } }))}
                    className="h-10 w-16 border rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={planModal.plan?.couleur || '#6366F1'}
                    onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, couleur: e.target.value } }))}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                </div>
              </div>
              
              {/* Ordre et Populaire */}
              <div className="flex gap-4 items-center">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
                  <input
                    type="number"
                    value={planModal.plan?.display_order || 1}
                    onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, display_order: parseInt(e.target.value) || 1 } }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-6">
                  <input
                    type="checkbox"
                    checked={planModal.plan?.is_popular === 1}
                    onChange={(e) => setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, is_popular: e.target.checked ? 1 : 0 } }))}
                    className="w-5 h-5 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">⭐ Recommandé</span>
                </label>
              </div>
              
              {/* Modules */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Modules inclus</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded-xl bg-gray-50 max-h-40 overflow-y-auto">
                  {modules.map(mod => (
                    <label key={mod.code} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded-lg">
                      <input
                        type="checkbox"
                        checked={planModal.plan?.modules?.includes(mod.code)}
                        onChange={(e) => {
                          const currentModules = planModal.plan?.modules || [];
                          if (e.target.checked) {
                            setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, modules: [...currentModules, mod.code] } }));
                          } else {
                            setPlanModal(prev => ({ ...prev, plan: { ...prev.plan, modules: currentModules.filter(m => m !== mod.code) } }));
                          }
                        }}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-lg">{mod.icone}</span>
                      <span className="text-sm text-gray-700">{mod.nom}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setPlanModal({ show: false, plan: null })}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={submitCreatePlan}
                className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition"
              >
                Créer le pack
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">📦 Gestion des Abonnements</h1>
          <p className="text-gray-600 mt-2">Gérez les packs, modules, périodes et abonnements utilisateurs</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-sm text-gray-500">Abonnements actifs</div>
              <div className="text-3xl font-bold text-green-600">{stats.global?.active_count || 0}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-sm text-gray-500">En attente</div>
              <div className="text-3xl font-bold text-orange-600">{stats.global?.pending_count || 0}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-sm text-gray-500">Expirés</div>
              <div className="text-3xl font-bold text-red-600">{stats.global?.expired_count || 0}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-500">Revenus totaux</div>
                <select
                  value={revenuePeriod}
                  onChange={(e) => setRevenuePeriod(e.target.value)}
                  className="text-xs border rounded-lg px-2 py-1 text-gray-600 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">Tout</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">7 derniers jours</option>
                  <option value="month">Ce mois</option>
                  <option value="quarter">3 derniers mois</option>
                  <option value="year">Cette année</option>
                  <option value="custom">📅 Personnalisé</option>
                </select>
              </div>
              {revenuePeriod === 'custom' && (
                <div className="flex gap-2 mb-2">
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="text-xs border rounded-lg px-2 py-1 flex-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-gray-400 self-center">→</span>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="text-xs border rounded-lg px-2 py-1 flex-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              )}
              <div className="text-3xl font-bold text-purple-600">
                {Number(stats.global?.total_revenue || 0).toLocaleString()} <span className="text-lg">XOF</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b">
          {[
            { id: 'plans', label: '📦 Packs', count: plans.length, hideForAccountant: true },
            { id: 'periods', label: '📅 Périodes', count: periods.length, hideForAccountant: true },
            { id: 'pending', label: '⏳ En attente', count: stats?.global?.pending_count || 0 },
            { id: 'all', label: '📋 Tous', count: null },
            { id: 'modules', label: '🧩 Modules', count: modules.length, hideForAccountant: true },
          ]
          .filter(t => !(t.hideForAccountant && adminRole === 'accountant'))
          .map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.count !== null && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  tab === t.id ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab: Packs */}
        {tab === 'plans' && (
          <div>
            {/* Bouton Créer un pack */}
            <div className="mb-6 flex justify-end">
              <button
                onClick={handleCreatePlan}
                className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition flex items-center gap-2"
              >
                ➕ Nouveau pack
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map(plan => (
              <div 
                key={plan.id} 
                className={`bg-white rounded-2xl shadow-sm overflow-hidden border-2 ${
                  plan.is_popular ? 'border-purple-500' : 'border-transparent'
                }`}
              >
                {plan.is_popular && (
                  <div className="bg-purple-500 text-white text-center py-1 text-sm font-medium">
                    ⭐ Recommandé
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{plan.icone}</span>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{plan.nom}</h3>
                        <span 
                          className="px-2 py-0.5 text-xs rounded-full text-white"
                          style={{ backgroundColor: plan.couleur }}
                        >
                          {plan.code}
                        </span>
                      </div>
                    </div>
                    {/* Bouton Supprimer */}
                    <button
                      onClick={() => handleDeletePlan(plan)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Supprimer ce pack"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                  
                  {editingPlan?.id === plan.id ? (
                    <div className="space-y-3 mb-4">
                      {/* Nom */}
                      <div>
                        <label className="text-xs text-gray-500">Nom du pack</label>
                        <input
                          type="text"
                          value={editingPlan.nom}
                          onChange={(e) => setEditingPlan({...editingPlan, nom: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <label className="text-xs text-gray-500">Description</label>
                        <textarea
                          value={editingPlan.description || ''}
                          onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg resize-none"
                          rows={2}
                        />
                      </div>
                      
                      {/* Icône et Couleur */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Icône (emoji)</label>
                          <input
                            type="text"
                            value={editingPlan.icone || ''}
                            onChange={(e) => setEditingPlan({...editingPlan, icone: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg text-center text-2xl"
                            maxLength={4}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Couleur</label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={editingPlan.couleur || '#8B5CF6'}
                              onChange={(e) => setEditingPlan({...editingPlan, couleur: e.target.value})}
                              className="w-12 h-10 border rounded-lg cursor-pointer"
                            />
                            <input
                              type="text"
                              value={editingPlan.couleur || ''}
                              onChange={(e) => setEditingPlan({...editingPlan, couleur: e.target.value})}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                              placeholder="#HEX"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Prix */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Prix mensuel (XOF)</label>
                          <input
                            type="number"
                            value={editingPlan.prix_mensuel}
                            onChange={(e) => setEditingPlan({...editingPlan, prix_mensuel: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Prix annuel (XOF)</label>
                          <input
                            type="number"
                            value={editingPlan.prix_annuel || ''}
                            onChange={(e) => setEditingPlan({...editingPlan, prix_annuel: e.target.value || null})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                      
                      {/* Options */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Ordre d'affichage</label>
                          <input
                            type="number"
                            value={editingPlan.display_order || 0}
                            onChange={(e) => setEditingPlan({...editingPlan, display_order: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg"
                            min={0}
                          />
                        </div>
                        <div className="flex flex-col justify-end pb-1">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingPlan.is_popular === 1}
                              onChange={(e) => setEditingPlan({...editingPlan, is_popular: e.target.checked ? 1 : 0})}
                              className="w-4 h-4 text-purple-600"
                            />
                            <span className="text-sm text-gray-600">⭐ Pack recommandé</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Modules */}
                      <div>
                        <label className="text-xs text-gray-500 mb-2 block">Modules inclus</label>
                        <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                          {modules.map(mod => (
                            <label key={mod.code} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                              <input
                                type="checkbox"
                                checked={editingPlan.modules?.includes(mod.code)}
                                onChange={(e) => {
                                  const currentModules = editingPlan.modules || [];
                                  if (e.target.checked) {
                                    setEditingPlan({...editingPlan, modules: [...currentModules, mod.code]});
                                  } else {
                                    setEditingPlan({...editingPlan, modules: currentModules.filter(m => m !== mod.code)});
                                  }
                                }}
                                className="w-4 h-4 text-purple-600"
                              />
                              <span className="text-lg">{mod.icone}</span>
                              <span className="text-sm text-gray-700">{mod.nom}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Boutons */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleUpdatePlan(plan.id)}
                          className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
                        >
                          ✓ Sauvegarder
                        </button>
                        <button
                          onClick={() => setEditingPlan(null)}
                          className="px-4 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold" style={{ color: plan.couleur }}>
                          {Number(plan.prix_mensuel).toLocaleString()}
                        </span>
                        <span className="text-gray-500">XOF/mois</span>
                      </div>
                      {plan.prix_annuel && (
                        <div className="text-sm text-gray-500">
                          ou {Number(plan.prix_annuel).toLocaleString()} XOF/an
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="border-t pt-4 mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Modules inclus:</div>
                    <ul className="space-y-2">
                      {plan.modules?.map((moduleCode, idx) => (
                        <li key={moduleCode} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-green-500">✓</span>
                          {plan.module_names?.[idx] || moduleCode}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Stats par plan */}
                  {stats?.by_plan && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <div className="text-sm text-gray-500">Abonnés actifs</div>
                      <div className="text-xl font-bold text-gray-900">
                        {stats.by_plan.find(s => s.code === plan.code)?.count || 0}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setEditingPlan({...plan})}
                    className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ✏️ Modifier
                  </button>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}

        {/* Tab: Pending / All */}
        {(tab === 'pending' || tab === 'all') && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Filtres */}
            <div className="p-4 border-b space-y-4">
              <div className="flex flex-wrap gap-4">
                {/* Recherche */}
                <div className="flex-1 min-w-64">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher par nom, email ou pack..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                {/* Filtre par statut */}
                {tab === 'all' && (
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="all">📊 Tous les statuts</option>
                    <option value="active">✅ Actifs</option>
                    <option value="pending">⏳ En attente</option>
                    <option value="expired">❌ Expirés</option>
                    <option value="cancelled">🚫 Annulés</option>
                  </select>
                )}
                
                {/* Filtre par pack */}
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="all">📦 Tous les packs</option>
                  {plans.map(p => (
                    <option key={p.id} value={p.id}>{p.icone} {p.nom}</option>
                  ))}
                </select>
                
                {/* Items par page */}
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
              
              {/* Résumé des résultats */}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>
                  {filteredSubscriptions.length} résultat{filteredSubscriptions.length > 1 ? 's' : ''} 
                  {search && ` pour "${search}"`}
                </span>
                {filteredSubscriptions.length > 0 && (
                  <span>
                    Page {currentPage} / {totalPages}
                  </span>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Pack</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Durée</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Montant</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Statut</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedSubscriptions.map(sub => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {sub.user_photo ? (
                            <img 
                              src={`${UPLOADS_URL}/${sub.user_photo}`} 
                              alt="" 
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                              {sub.user_nom?.[0]}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{sub.user_nom}</div>
                            <div className="text-sm text-gray-500">{sub.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className="px-2 py-1 text-xs rounded-full text-white"
                          style={{ backgroundColor: sub.plan_couleur }}
                        >
                          {sub.plan_nom}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {sub.duration_months} mois
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {Number(sub.amount_paid).toLocaleString()} XOF
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          sub.status === 'active' ? 'bg-green-100 text-green-700' :
                          sub.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                          sub.status === 'expired' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {sub.status === 'active' ? 'Actif' :
                           sub.status === 'pending' ? 'En attente' :
                           sub.status === 'expired' ? 'Expiré' : sub.status}
                        </span>
                        {sub.days_remaining !== null && sub.status === 'active' && (
                          <div className="text-xs text-gray-500 mt-1">
                            {sub.days_remaining > 0 ? `${sub.days_remaining}j restants` : 'Expire aujourd\'hui'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        {sub.status === 'pending' && (
                          <div className="flex gap-2">
                            {sub.payment_proof && (
                              <a
                                href={`${UPLOADS_URL}/${sub.payment_proof}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                📎 Preuve
                              </a>
                            )}
                            <button
                              onClick={() => handleApprove(sub.id)}
                              className="text-green-600 hover:text-green-800"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => handleReject(sub.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {paginatedSubscriptions.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        {search || statusFilter !== 'all' || planFilter !== 'all' 
                          ? 'Aucun abonnement ne correspond aux filtres'
                          : 'Aucun abonnement trouvé'
                        }
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredSubscriptions.length)} sur {filteredSubscriptions.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    «
                  </button>
                  
                  {/* Numéros de page */}
                  {(() => {
                    const pages = [];
                    const showPages = new Set();
                    showPages.add(1);
                    showPages.add(totalPages);
                    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                      showPages.add(i);
                    }
                    const sortedPages = [...showPages].sort((a, b) => a - b);
                    sortedPages.forEach((page, idx) => {
                      if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                        pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                      }
                      pages.push(
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded border ${currentPage === page ? 'bg-purple-600 text-white border-purple-600' : 'bg-white hover:bg-gray-100'}`}
                        >
                          {page}
                        </button>
                      );
                    });
                    return pages;
                  })()}
                  
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    »
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Modules */}
        {tab === 'modules' && (() => {
          // Filtrer les modules
          const filteredModules = modules.filter(mod => {
            if (!moduleSearch) return true;
            const searchLower = moduleSearch.toLowerCase();
            return (
              mod.nom?.toLowerCase().includes(searchLower) ||
              mod.code?.toLowerCase().includes(searchLower) ||
              mod.description?.toLowerCase().includes(searchLower) ||
              mod.route_pattern?.toLowerCase().includes(searchLower)
            );
          });

          // Pagination
          const moduleTotalPages = Math.ceil(filteredModules.length / moduleItemsPerPage);
          const paginatedModules = filteredModules.slice(
            (moduleCurrentPage - 1) * moduleItemsPerPage,
            moduleCurrentPage * moduleItemsPerPage
          );

          return (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                  <h3 className="font-medium text-gray-900">Modules disponibles</h3>
                  <p className="text-sm text-gray-500">Fonctionnalités activables par pack d'abonnement</p>
                </div>
                <button 
                  onClick={handleCreateModule}
                  className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600"
                >
                  + Ajouter un module
                </button>
              </div>

              {/* Barre de recherche et contrôles */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  {/* Recherche */}
                  <div className="relative flex-1 w-full md:max-w-md">
                    <input
                      type="text"
                      placeholder="Rechercher par nom, code, description..."
                      value={moduleSearch}
                      onChange={(e) => {
                        setModuleSearch(e.target.value);
                        setModuleCurrentPage(1);
                      }}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  </div>

                  {/* Items par page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Afficher:</span>
                    <select
                      value={moduleItemsPerPage}
                      onChange={(e) => {
                        setModuleItemsPerPage(Number(e.target.value));
                        setModuleCurrentPage(1);
                      }}
                      className="border rounded-lg px-2 py-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {/* Compteur de résultats */}
                <div className="mt-2 text-sm text-gray-500">
                  {filteredModules.length} module{filteredModules.length > 1 ? 's' : ''} trouvé{filteredModules.length > 1 ? 's' : ''}
                  {moduleSearch && ` pour "${moduleSearch}"`}
                </div>
              </div>

              <div className="divide-y">
                {paginatedModules.map(mod => (
                  <div key={mod.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{mod.icone}</span>
                      <div>
                        <div className="font-medium text-gray-900">{mod.nom}</div>
                        <div className="text-sm text-gray-500">{mod.description}</div>
                        <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                          <div>Code: <code className="bg-gray-100 px-1 rounded">{mod.code}</code></div>
                          {mod.route_pattern && (
                            <div>🔐 Gestion: <code className="bg-blue-50 text-blue-700 px-1 rounded">{mod.route_pattern}</code></div>
                          )}
                          {mod.public_route_pattern && (
                            <div>🌐 Public: <code className="bg-green-50 text-green-700 px-1 rounded">{mod.public_route_pattern}</code></div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleToggleModuleActive(mod)}
                        className={`px-3 py-1 text-xs rounded-full cursor-pointer transition-colors ${
                          mod.is_active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {mod.is_active ? '✓ Actif' : '○ Inactif'}
                      </button>
                      <button 
                        onClick={() => handleEditModule(mod)}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleDeleteModule(mod)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
                {paginatedModules.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {moduleSearch ? 'Aucun module ne correspond à votre recherche' : 'Aucun module configuré'}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {moduleTotalPages > 1 && (
                <div className="p-4 border-t bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Page {moduleCurrentPage} sur {moduleTotalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setModuleCurrentPage(1)}
                      disabled={moduleCurrentPage === 1}
                      className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      ««
                    </button>
                    <button
                      onClick={() => setModuleCurrentPage(p => Math.max(1, p - 1))}
                      disabled={moduleCurrentPage === 1}
                      className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      «
                    </button>
                    
                    {/* Numéros de pages */}
                    {(() => {
                      const pages = [];
                      let startPage = Math.max(1, moduleCurrentPage - 2);
                      let endPage = Math.min(moduleTotalPages, startPage + 4);
                      if (endPage - startPage < 4) {
                        startPage = Math.max(1, endPage - 4);
                      }
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setModuleCurrentPage(i)}
                            className={`px-3 py-1 border rounded-lg text-sm ${
                              i === moduleCurrentPage
                                ? 'bg-purple-500 text-white border-purple-500'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                    
                    <button
                      onClick={() => setModuleCurrentPage(p => Math.min(moduleTotalPages, p + 1))}
                      disabled={moduleCurrentPage === moduleTotalPages}
                      className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      »
                    </button>
                    <button
                      onClick={() => setModuleCurrentPage(moduleTotalPages)}
                      disabled={moduleCurrentPage === moduleTotalPages}
                      className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      »»
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Tab: Périodes */}
        {tab === 'periods' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="font-medium text-gray-900">Périodes d'abonnement</h3>
                <p className="text-sm text-gray-500">Configurez les durées et réductions disponibles</p>
              </div>
              <button 
                onClick={handleCreatePeriod}
                className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600"
              >
                + Ajouter une période
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Mois</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Label</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Réduction</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ordre</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Statut</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {periods.map(period => (
                    <tr key={period.id} className="hover:bg-gray-50">
                      {editingPeriod?.id === period.id ? (
                        <>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={editingPeriod.months}
                              onChange={(e) => setEditingPeriod({...editingPeriod, months: parseInt(e.target.value)})}
                              className="w-20 px-2 py-1 border rounded"
                              min="1"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editingPeriod.label}
                              onChange={(e) => setEditingPeriod({...editingPeriod, label: e.target.value})}
                              className="w-32 px-2 py-1 border rounded"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editingPeriod.discount}
                                onChange={(e) => setEditingPeriod({...editingPeriod, discount: parseFloat(e.target.value)})}
                                className="w-20 px-2 py-1 border rounded"
                                min="0"
                                max="100"
                                step="0.5"
                              />
                              <span>%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={editingPeriod.display_order || 0}
                              onChange={(e) => setEditingPeriod({...editingPeriod, display_order: parseInt(e.target.value)})}
                              className="w-16 px-2 py-1 border rounded"
                              min="0"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editingPeriod.is_active}
                                onChange={(e) => setEditingPeriod({...editingPeriod, is_active: e.target.checked})}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Actif</span>
                            </label>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUpdatePeriod(period.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => setEditingPeriod(null)}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {period.months} mois
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {period.label}
                          </td>
                          <td className="px-4 py-3">
                            {period.discount > 0 ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                -{period.discount}%
                              </span>
                            ) : (
                              <span className="text-gray-400">Aucune</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {period.display_order || 0}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              period.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {period.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingPeriod({...period})}
                                className="text-blue-600 hover:text-blue-800"
                                title="Modifier"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeletePeriod(period.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Supprimer"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {periods.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                        Aucune période configurée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Info box */}
            <div className="p-4 bg-blue-50 border-t border-blue-100">
              <p className="text-sm text-blue-800">
                💡 <strong>Astuce :</strong> Les réductions s'appliquent automatiquement sur le prix total de la période. 
                Par exemple, une réduction de 10% sur 6 mois réduit le prix de 10% par rapport au tarif mensuel × 6.
              </p>
            </div>
          </div>
        )}

        {/* Modal Module */}
        {moduleModal.show && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 animate-scale-in">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {moduleModal.module?.id ? '✏️ Modifier le module' : '➕ Nouveau module'}
              </h3>
              
              <div className="space-y-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code unique <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={moduleModal.module?.code || ''}
                    onChange={(e) => setModuleModal({
                      ...moduleModal,
                      module: { ...moduleModal.module, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="ex: shop, workspace, premium_support"
                    disabled={!!moduleModal.module?.id}
                  />
                  {moduleModal.module?.id && (
                    <p className="text-xs text-gray-400 mt-1">Le code ne peut pas être modifié</p>
                  )}
                </div>
                
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={moduleModal.module?.nom || ''}
                    onChange={(e) => setModuleModal({
                      ...moduleModal,
                      module: { ...moduleModal.module, nom: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="ex: Boutique en ligne"
                  />
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={moduleModal.module?.description || ''}
                    onChange={(e) => setModuleModal({
                      ...moduleModal,
                      module: { ...moduleModal.module, description: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    rows={2}
                    placeholder="Description du module..."
                  />
                </div>
                
                {/* Icône et Route */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icône (emoji)</label>
                    <input
                      type="text"
                      value={moduleModal.module?.icone || ''}
                      onChange={(e) => setModuleModal({
                        ...moduleModal,
                        module: { ...moduleModal.module, icone: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg text-center text-2xl"
                      maxLength={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Route gestion (propriétaire)</label>
                    <input
                      type="text"
                      value={moduleModal.module?.route_pattern || ''}
                      onChange={(e) => setModuleModal({
                        ...moduleModal,
                        module: { ...moduleModal.module, route_pattern: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="/showcase, /shop..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Route pour l'espace de gestion</p>
                  </div>
                </div>

                {/* Route publique */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Route publique (visiteurs)</label>
                  <input
                    type="text"
                    value={moduleModal.module?.public_route_pattern || ''}
                    onChange={(e) => setModuleModal({
                      ...moduleModal,
                      module: { ...moduleModal.module, public_route_pattern: e.target.value }
                    })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="/site/*, /boutique/*..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Route accessible aux visiteurs (utiliser * comme wildcard). Laissez vide si pas de partie publique.</p>
                </div>
                
                {/* Actif */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="moduleActive"
                    checked={moduleModal.module?.is_active ?? true}
                    onChange={(e) => setModuleModal({
                      ...moduleModal,
                      module: { ...moduleModal.module, is_active: e.target.checked }
                    })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <label htmlFor="moduleActive" className="text-sm text-gray-700">Module actif</label>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setModuleModal({ show: false, module: null })}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveModule}
                  className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
                >
                  {moduleModal.module?.id ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptionPlans;
